import { ATHENS_GRAY } from "@/shared/colors";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import { Badge, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import {
	EmployeeField,
	EmployeeNumberProvider,
} from "./employee/EmployeeNumber";
import { MaterialTab } from "./material/MaterialTab";
import { useSawEntryQueue } from "./material/hooks/useSawEntryQueue";
import { BundleQualityCheckTab } from "./quality-check/BundleQualityCheckTab";
import { useSawBundlesByShift } from "@/pages/sawlines/hooks/useSawBundlesByShift";

interface Props {
	assetId: number;
	assetName: string;
}

export const SawTabletWorkspace = ({ assetId, assetName }: Props) => {
	const [tab, setTab] = useState(0);
	const quality = useSawBundlesByShift(assetId);
	const material = useSawEntryQueue(assetId);
	const tabs = [
		{
			id: "qc",
			label: "Bundle Quality Check",
			Icon: FactCheckOutlined,
			badge: { color: "error" as const },
			count: quality.isError ? undefined : quality.data?.pendingCount,
			panel: <BundleQualityCheckTab assetId={assetId} />,
		},
		{
			id: "material",
			label: "Material",
			Icon: Inventory2Outlined,
			badge: {
				sx: { "& .MuiBadge-badge": { bgcolor: "#546e7a", color: "#fff" } },
			},
			count: material.isError ? undefined : material.data?.entries.length,
			panel: <MaterialTab assetId={assetId} />,
		},
	];

	return (
		<EmployeeNumberProvider shiftId={quality.data?.shift.ShiftId}>
			<Box
				sx={{
					height: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 64px)" },
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					spacing={{ xs: 1, md: 3 }}
					sx={{
						px: { xs: 1.5, md: 2.5 },
						flexShrink: 0,
						minHeight: 64,
						backgroundColor: "background.paper",
						borderBottom: 1,
						borderColor: "divider",
					}}
				>
					<Box sx={{ flexShrink: 0 }}>
						<Typography component="h1" variant="subtitle1" fontWeight={700}>
							Saw Tablet
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{assetName}
						</Typography>
					</Box>
					<Tabs
						value={tab}
						onChange={(_, value: number) => setTab(value)}
						aria-label="Saw Tablet"
						variant="scrollable"
						sx={{
							minWidth: 0,
							"& .MuiTab-root": { minHeight: 64, minWidth: 170, py: 0.75 },
						}}
					>
						{tabs.map(({ id, label, Icon, badge, count }) => (
							<Tab
								key={id}
								iconPosition="top"
								// Badge hides itself at 0
								icon={
									<Badge {...badge} badgeContent={count}>
										<Icon />
									</Badge>
								}
								label={label}
								id={`saw-tablet-tab-${id}`}
								aria-controls={`saw-tablet-panel-${id}`}
							/>
						))}
					</Tabs>
					<Box sx={{ flex: 1 }} />
					<EmployeeField />
				</Stack>
				{tabs.map(({ id, panel }, index) => (
					<Box
						key={id}
						role="tabpanel"
						id={`saw-tablet-panel-${id}`}
						aria-labelledby={`saw-tablet-tab-${id}`}
						hidden={tab !== index}
						sx={{
							display: tab === index ? "flex" : "none",
							flex: 1,
							minHeight: 0,
							p: 1.5,
						}}
					>
						{panel}
					</Box>
				))}
			</Box>
		</EmployeeNumberProvider>
	);
};
